// Duration + candidate-estimate formatting shared by the dashboard and every
// surface that tells a candidate how long a document takes.

/** Exact-ish duration for analytics: 45s, 4m 12s, 2h 5m. */
export function formatDuration(totalSeconds) {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) return '—';
  const s = Math.round(totalSeconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return rem ? `${m}m ${rem}s` : `${m}m`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

// Whole minutes for display, never below 1 (sub-minute handled separately).
const minutesFor = (seconds) => Math.max(1, Math.round(seconds / 60));

/**
 * Candidate-facing phrase for ONE document, or null when there is no estimate.
 * "Under 1 minute" / "About 4 minutes" / "About 2 hours".
 */
export function formatEstimate(seconds) {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) return 'Under 1 minute';
  const minutes = minutesFor(seconds);
  if (minutes >= 90) {
    const hours = Math.round(minutes / 30) / 2;
    return `About ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  return `About ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
}

/**
 * Total for a packet's documents (anything with `estimated_seconds`).
 * Returns null when no document has an estimate. If only some do, the phrase
 * says so ("... for 2 of 3 documents") rather than presenting a partial sum
 * as the whole packet — an under-estimate is worse than none.
 */
export function formatPacketEstimate(docs) {
  const list = Array.isArray(docs) ? docs : [];
  const timed = list.filter((d) => Number.isFinite(d?.estimated_seconds) && d.estimated_seconds > 0);
  if (!timed.length) return null;
  const total = timed.reduce((sum, d) => sum + d.estimated_seconds, 0);
  const base = formatEstimate(total);
  if (timed.length === list.length) return `${base} in total`;
  return `${base} for ${timed.length} of ${list.length} documents`;
}
